import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  LoadingButton,
} from '@one-thd/sui-atomic-components';
import { QuantityItem } from '@src/components/quantity-selection/QuantityItem';
import { ItemToReturn } from '@src/models/returns';
import { setUpdatedItem, useReturnStore } from '@src/stores/returnStore';

interface EditItemDrawerProps {
  displayDrawer: boolean;
  closeDrawer: () => void;
  item: ItemToReturn;
  onEditItemSubmit: (
    startingState: ItemToReturn,
    closeDrawer: () => void,
  ) => void;
  isEditSubmitting: boolean;
}

const EditItemDrawer = ({
  displayDrawer,
  closeDrawer,
  item,
  onEditItemSubmit,
  isEditSubmitting,
}: EditItemDrawerProps) => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  // starting state for review item drawer
  // Update starting state when onMount
  const [startingState, setStartingState] = useState<ItemToReturn>(item);
  const [hasWeightError, setHasWeightError] = useState<string | null>(null);
  // checks if item (which gets updated automatically) has changed from starting state
  const hasItemChanged = useMemo(() => {
    return JSON.stringify(item) !== JSON.stringify(startingState);
  }, [item, startingState]);

  const onCancelUpdate = () => {
    // Refused while the change is being submitted. Backing out here reverts the
    // item and closes the drawer, but the calls already in flight carry on and
    // still finish — so the customer would watch their change be undone and
    // then reappear, and a second submission could be started over the first.
    if (isEditSubmitting) return;
    // revert to starting state if user cancels out of drawer
    setUpdatedItem(startingState);
    closeDrawer();
  };
  const onSubmitUpdate = () => {
    // Don't proceed if there's a weight error
    if (hasWeightError) return;
    // Don't proceed if quantity is less than 1
    if (item.returnQuantity < 1) return;
    onEditItemSubmit(startingState, closeDrawer);
  };

  useEffect(() => {
    if (displayDrawer) {
      setHasWeightError(null);
      setStartingState(item);
    }
  }, [displayDrawer]);

  return (
    <Drawer
      open={displayDrawer}
      onClose={onCancelUpdate}
      // Both of the drawer's own ways out — the backdrop and Escape — land on
      // the same guarded handler, but the key has to be stopped at the source
      // as well, or it closes the drawer without asking the handler at all.
      disableEscapeKeyDown={isEditSubmitting}
      position="right"
      className="drawer-z-index-fix"
    >
      <DrawerHeader title="Edit Return Item" onClose={onCancelUpdate} />
      <DrawerBody>
        <QuantityItem
          item={item}
          selectedItems={selectedItems}
          isHrtvIneligible={false}
          setHasWeightError={setHasWeightError}
          hasWeightError={hasWeightError}
        />
      </DrawerBody>
      <DrawerFooter className="sui-bg-primary sui-p-4 sui-relative sui-bottom-0 sui-sticky sui-shadow-sm">
        <div className="sui-flex sui-gap-4 sui-flex-col">
          <LoadingButton
            fullWidth
            loading={isEditSubmitting}
            onClick={onSubmitUpdate}
            disabled={!hasItemChanged}
          >
            Update Return item
          </LoadingButton>
          <Button
            fullWidth
            variant="secondary"
            onClick={onCancelUpdate}
            disabled={isEditSubmitting}
          >
            Cancel
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default EditItemDrawer;
